import React, { useState } from 'react';
import { X, CircleNotch, Key } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

export default function ChangePasswordModal({ isOpen, onClose, isRequired = false }) {
    const { changePassword } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (newPassword.length < 6) {
            setError('Nowe hasło musi mieć co najmniej 6 znaków');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Nowe hasła nie pasują');
            return;
        }

        setLoading(true);
        try {
            await changePassword(oldPassword, newPassword);
            alert('Hasło zostało pomyślnie zmienione!');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Błąd podczas zmiany hasła');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-sm p-6 w-full max-w-md mx-4 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-primary" />
                        <h2 className="text-xl font-heading font-bold">
                            {isRequired ? 'Zmień hasło (wymagane)' : 'Zmień hasło'}
                        </h2>
                    </div>
                    {!isRequired && (
                        <button
                            onClick={onClose}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {isRequired && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
                        <p className="text-sm text-yellow-800">
                            Ze względów bezpieczeństwa musisz zmienić hasło przy pierwszym logowaniu.
                        </p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="field-label">Stare hasło</label>
                        <input
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="input-field"
                            required
                            autoFocus
                            data-testid="old-password-input"
                        />
                    </div>

                    <div>
                        <label className="field-label">Nowe hasło (min. 6 znaków)</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="input-field"
                            required
                            minLength={6}
                            data-testid="new-password-input"
                        />
                    </div>

                    <div>
                        <label className="field-label">Potwierdź nowe hasło</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input-field"
                            required
                            data-testid="confirm-password-input"
                        />
                    </div>

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary flex-1 flex items-center justify-center gap-2"
                            data-testid="change-password-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <CircleNotch className="w-4 h-4 animate-spin" />
                                    Zmiana...
                                </>
                            ) : (
                                'Zmień hasło'
                            )}
                        </button>
                        {!isRequired && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-secondary"
                                disabled={loading}
                            >
                                Anuluj
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
